import { useEffect } from 'react';
import { Outlet, useParams } from 'react-router-dom';
import { useLedgerDetailBatch, type ExpandedLedger } from '@/api/hooks/useLedgerDetailBatch';
import { Layout } from '@/components/layout/Layout';
import { LedgerHeader } from './LedgerHeader';
import { LedgerDetailBar } from './StickyBackBar';
import { TabNavigation } from './TabNavigation';
import { ErrorAlert } from '@/components/ui/ErrorAlert';
import { PageLoadingState } from '@/components/ui/PageLoadingState';
import { getErrorMessage, isNotFoundError } from '@/utils/errorHandler';
import { usePageTitle } from '@/hooks/usePageTitle';
import { NotFoundPage } from '@/pages/NotFoundPage';

/**
 * Context type for child routes to access ledger data.
 */
export interface LedgerDetailOutletContext {
    ledger: ExpandedLedger;
    userLatestConfirmationDate: number | null;
}

/**
 * Main page component for viewing and managing a single ledger.
 * Displays ledger header with actions, tab navigation, and content area.
 * Routes to appropriate tab content (Values, Cash Flow, Analytics).
 *
 * Uses a single batch request to fetch ledger (with expanded collection/tags) and initial values.
 */
export function LedgerDetailPage() {
    const { id } = useParams<{ id: string }>();
    const ledgerId = Number(id);

    // Fetch ledger (with expanded collection/tags) and initial values in a single batch
    const { data, isLoading, error } = useLedgerDetailBatch(ledgerId);

    // Extract data from batch response
    const ledger = data?.ledger;
    const collection = ledger?.collection;
    const ledgerTags = ledger?.tags || [];

    // Set page title to ledger label when loaded
    usePageTitle(ledger?.label ?? null);

    // Scroll to top when navigating to this page
    useEffect(() => {
        window.scrollTo(0, 0);
    }, [ledgerId]);

    // Determine back path based on ledger type
    const backPath = ledger?.type === 'asset' ? '/assets' : '/debts';

    if (isLoading) {
        return (
            <Layout>
                <PageLoadingState />
            </Layout>
        );
    }

    // Handle 404 errors by showing NotFoundPage
    if (error && isNotFoundError(error)) {
        return <NotFoundPage />;
    }

    // Handle other errors
    if (error) {
        return (
            <Layout>
                <div className="space-y-6">
                    <ErrorAlert message={getErrorMessage(error)} />
                </div>
            </Layout>
        );
    }

    // Handle case where API succeeded but ledger doesn't exist
    if (!ledger) {
        return <NotFoundPage />;
    }

    return (
        <Layout>
            {/* Sticky breadcrumb bar with actions */}
            <LedgerDetailBar
                destinationLabel={ledger.type === 'asset' ? 'Assets' : 'Debts'}
                backPath={backPath}
                ledger={ledger}
            />

            <div className="space-y-6 pt-4">
                {/* Header with ledger info */}
                <LedgerHeader
                    ledger={ledger}
                    collection={collection}
                    tags={ledgerTags}
                />

                {/* Tab navigation */}
                <TabNavigation ledgerType={ledger.type} />

                {/* Tab content (rendered via nested routes) */}
                <div className="py-2">
                    <Outlet context={{
                        ledger,
                        userLatestConfirmationDate: data?.initialValues?.userLatestConfirmationDate ?? null,
                    } satisfies LedgerDetailOutletContext} />
                </div>
            </div>
        </Layout>
    );
}
